/**
 * GS-901 — a match time as an operator reads it: "Today · 15:30", "Yesterday · 21:00", "Tomorrow · 09:30", else
 * "12 Oct · 19:30". The servers send the database's wall clock ("2026-10-14 15:30:00.000"); a missing time is "Time not set".
 */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function whenText(startTime: string | null | undefined, now: Date = new Date()): string {
  if (!startTime) {
    return 'Time not set';
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(startTime);
  if (!m) {
    return startTime;
  }
  const day = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diff = Math.round((day.getTime() - today.getTime()) / 86400000);
  const time = m[4] + ':' + m[5];
  const label = diff === 0 ? 'Today' : diff === -1 ? 'Yesterday' : diff === 1 ? 'Tomorrow' : Number(m[3]) + ' ' + MONTHS[Number(m[2]) - 1];
  return label + ' · ' + time;
}

/** A date input's value (yyyy-MM-dd) for a day relative to today. */
export function isoDay(offsetDays = 0, now: Date = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offsetDays);
  const pad = (n: number) => (n < 10 ? '0' : '') + n;
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
}

/**
 * GS-902 — a fancy line's subtype, as live names it (FANCY-ENGINE.md; gs-bet-core FancyKind): the line is declared with
 * its NUMBER, and the lottery (5) reads its last digit, odd/even (6) its parity; every other subtype the number itself.
 */
const FANCY_NAMES: Record<number, string> = {
  1: 'Overs', 2: 'Batsman', 3: 'Single over', 4: 'Khadda', 5: 'Lottery', 6: 'Odd / even', 7: 'Three selections', 8: 'Ball by ball',
};

export function fancyName(subtype: number | null | undefined): string {
  return subtype ? FANCY_NAMES[subtype] || 'Fancy' : '';
}

/** How the line reads a declared number — "digit 7", "odd" — or '' where it reads the number itself (LineText's words). */
export function fancyView(value: number | null | undefined, subtype: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value) || value < 0) {
    return '';
  }
  if (subtype === 5) {
    return 'digit ' + (value % 10);
  }
  if (subtype === 6) {
    return value % 2 === 1 ? 'odd' : 'even';
  }
  return '';
}

/**
 * GS-894 — a premium selection's outcome in the statement's words (gs-settlement-service LineText): Won, Lost, Void,
 * Half won, Half lost, Dead heat 1/k.
 */
export function outcomeWords(outcome: string): string {
  switch (outcome) {
    case 'WIN':
      return 'Won';
    case 'LOSE':
      return 'Lost';
    case 'VOID':
      return 'Void';
    case 'HALF_WIN':
      return 'Half won';
    case 'HALF_LOSE':
      return 'Half lost';
    default:
      return outcome.indexOf('DEAD_HEAT_') === 0 ? 'Dead heat 1/' + outcome.substring('DEAD_HEAT_'.length) : outcome;
  }
}

/**
 * GS-894 — a premium result as the statement reads it (LineText.sharesText): every selection that did not simply lose,
 * by name, grouped by outcome in the market's order — "Sporting CP Won", "Over 2.25 Half won, Under 2.25 Half lost",
 * "A, B Dead heat 1/2"; "No winner" when every one lost. A name comes from the outcome itself or from {@code names}.
 */
export function outcomesText(outcomes: Array<{ selectionId: number; outcome: string; name?: string | null }>,
                             names: Record<number, string> = {}): string {
  const groups: Array<{ outcome: string; names: string[] }> = [];
  outcomes.forEach((o) => {
    if (o.outcome === 'LOSE') {
      return;
    }
    let group = groups.find((g) => g.outcome === o.outcome);
    if (!group) {
      group = { outcome: o.outcome, names: [] };
      groups.push(group);
    }
    group.names.push(o.name || names[o.selectionId] || String(o.selectionId));
  });
  return groups.length ? groups.map((g) => g.names.join(', ') + ' ' + outcomeWords(g.outcome)).join(', ') : 'No winner';
}

/** An instant as a datetime-local input's value, in this browser's time ("2026-10-14T19:32:05"). */
export function localInput(iso: string | null | undefined): string {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  if (isNaN(d.getTime())) {
    return '';
  }
  const pad = (n: number) => (n < 10 ? '0' : '') + n;
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) + 'T' + pad(d.getHours()) + ':' + pad(d.getMinutes())
    + ':' + pad(d.getSeconds());
}

/** A datetime-local value (this browser's time) as an ISO instant, or '' when empty or not a time. */
export function isoFromLocal(local: string): string {
  if (!local) {
    return '';
  }
  const d = new Date(local);
  return isNaN(d.getTime()) ? '' : d.toISOString();
}

/** An instant as a short local clock ("19:32:05"), with the day when it is not today. */
export function clockText(iso: string | null | undefined): string {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  if (isNaN(d.getTime())) {
    return iso;
  }
  const pad = (n: number) => (n < 10 ? '0' : '') + n;
  const time = pad(d.getHours()) + ':' + pad(d.getMinutes()) + ':' + pad(d.getSeconds());
  const today = new Date();
  return d.toDateString() === today.toDateString() ? time : d.getDate() + ' ' + MONTHS[d.getMonth()] + ' ' + time;
}
